import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface PolicyValue {
  readonly parameterCode: string;
  readonly value: string | null;
}

interface PolicyGroup {
  readonly group: string;
  readonly values: PolicyValue[];
}

/**
 * M44 §20 Commercial Governance Console (§28). Commercial governors review and
 * update the tenant-scoped commercial policy parameters (pricing / approval /
 * discount / billing / tax / revenue-recognition / audit). Viewing needs
 * M44_GOVERNANCE_VIEW; updating a policy needs M44_GOVERNANCE_MANAGE (SoD). No
 * value is hardcoded — every gate resolves through the M01 parameter service
 * (LAW-6).
 */
export function CommercialGovernanceConsole(): ReactNode {
  const { token } = useAuth();
  const [groups, setGroups] = useState<PolicyGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [parameterCode, setParameterCode] = useState('');
  const [value, setValue] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: PolicyGroup[] }>('/v1/billing/governance/policies', { token })
      .then((r) => { if (alive) { setGroups(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  const update = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!parameterCode.trim()) { setActionError('A policy parameter code is required.'); return; }
    try {
      await apiRequest('/v1/billing/governance/policies', {
        method: 'POST',
        body: { parameterCode: parameterCode.trim(), value: value.trim() },
        token,
      });
      setNotice('Policy updated.');
      setValue('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised to manage governance (M44_GOVERNANCE_MANAGE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="commercial-governance-console">
      <h1>Commercial Governance Console</h1>

      <section aria-label="governance-update">
        <h2>Update Policy</h2>
        <form aria-label="governance-form" onSubmit={(e) => void update(e)}>
          <label>Parameter Code<input aria-label="field-parameter-code" value={parameterCode} onChange={(e) => setParameterCode(e.target.value)} /></label>
          <label>Value<input aria-label="field-value" value={value} onChange={(e) => setValue(e.target.value)} /></label>
          <button type="submit" aria-label="submit-policy">Save</button>
        </form>
        {actionError && <p role="alert" aria-label="governance-action-error">{actionError}</p>}
        {notice && <p aria-label="governance-notice">{notice}</p>}
      </section>

      {error && <p role="alert" aria-label="governance-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <section aria-label="governance-policies">
          {groups.length === 0 ? <p>No policy groups.</p> : groups.map((g) => (
            <div key={g.group} data-group={g.group}>
              <h2 aria-label={`group-${g.group}`}>{g.group}</h2>
              <table aria-label={`group-table-${g.group}`}>
                <thead>
                  <tr><th scope="col">Parameter</th><th scope="col">Value</th></tr>
                </thead>
                <tbody>
                  {g.values.map((v) => (
                    <tr key={v.parameterCode} data-policy={v.parameterCode}>
                      <td aria-label={`policy-code-${v.parameterCode}`}>{v.parameterCode}</td>
                      <td aria-label={`policy-value-${v.parameterCode}`}>{v.value ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>
      )}
    </main>
  );
}
